import React, { useState } from 'react';
import { cautionIconFilled, clinicIcon, infoIcon2, loaderIcon, tickIcon2 } from '../common/Icons';
import { getDoc } from '../../store/idbHandler';
import { track } from '../../tracking';
import pushRegisterABDMLead from '../../api/pushRegisterABDMLead';
import { captureError } from '../../pages/captureError';
import { dispatch as ekaDispatch } from '../../bridge/events/dispatch';
import { HPR_HFR_ONBOARDING } from '../../bridge/events/types';
import { fetchAndDownloadAbhaQrController } from './fetchAbhaQr';
import { notification } from 'antd';

const ClinicDetailsContainer: React.FC<{
    hideAbhaQRDownload?: boolean;
    cid?: string;
}> = ({ hideAbhaQRDownload, cid }) => {
    const [abhaQrStatus, setIsAbhaQrLoading] = useState<{
        status: boolean;
        cid?: string;
    }>({
        cid,
        status: false,
    });

    const onAbhaQrDownload = (clinicId: string) => {
        if (abhaQrStatus.status === true) {
            return;
        }

        setIsAbhaQrLoading((prev) => ({ ...prev, status: true }));

        if (clinicId) {
            fetchAndDownloadAbhaQrController(clinicId)
                .then(() => {
                    setIsAbhaQrLoading((prev) => ({ ...prev, status: false }));
                })
                .catch(() => {
                    notification.error({
                        message: 'Failed to download',
                        duration: 5,
                        placement: 'topRight',
                    });
                    setIsAbhaQrLoading((prev) => ({ ...prev, status: false }));
                });
        }
    };

    const registerNow = () => {
        pushRegisterABDMLead().catch(captureError);
        track('REGISTER_NOW_ABDM');
        ekaDispatch({ type: HPR_HFR_ONBOARDING });
    };

    const doc = getDoc();

    const hfIdClinics = doc?.profile?.professional?.clinics?.filter((c) => c?.abdm?.hfr_id) ?? [];

    const noHfrIdClinics =
        doc?.profile?.professional?.clinics?.filter((c) => !c?.abdm?.hfr_id) ?? [];

    return (
        <div className="rounded-8 pb-12 bg-white flex flex-col h-240 max-h-240 border border-darwin-neutral_new-200">
            <div className="flex rounded-t-8 p-12 gap-16 bg-darwin-neutral_new-50 flex-col items-start">
                <div className="flex items-center gap-8">
                    <span>{clinicIcon('w-24 h-24 fill-darwin-accent-bright-blue-800')}</span>
                    <p className="text-14 font-700">HFR & HPR details</p>
                </div>
            </div>
            <div className="flex m-16 gap-16 flex-col items-start">
                <div className="flex items-center gap-8">
                    <span>{infoIcon2('w-16 h-16')}</span>
                    <p className="text-14 font-400">
                        HPR ID: {doc?.profile.professional.hpr_id_number}
                    </p>
                </div>
            </div>
            <div className="flex overflow-scroll hide-scrollbar mx-16 gap-16 flex-col items-start">
                {[...noHfrIdClinics, ...hfIdClinics]?.map((c) => (
                    <div className="flex items-center w-full gap-8">
                        {c.abdm?.hfr_id ? (
                            <span className="border my-4 self-start border-darwin-green-primary flex items-center justify-center rounded-full">
                                {tickIcon2('fill-darwin-green-primary font-700 w-14 h-14')}
                            </span>
                        ) : (
                            <span className="self-start my-4">
                                {cautionIconFilled('w-16 h-16')}
                            </span>
                        )}
                        <div className="flex flex-col items-start gap-2">
                            <p className="text-14 font-400">{c.name}</p>
                            {c.abdm?.hfr_id ? (
                                <p className="text-13 font-400 max-w-241 break-all">
                                    HFR ID: {c.abdm?.hfr_id}
                                </p>
                            ) : (
                                <div className="flex items-center gap-8">
                                    <span className="text-13 font-400">Not registered on HFR.</span>
                                </div>
                            )}
                        </div>
                        {c.abdm?.hfr_id ? (
                            !hideAbhaQRDownload ? (
                                <span
                                    onClick={() => {
                                        setIsAbhaQrLoading((prev) => ({ ...prev, cid: c._id }));
                                        onAbhaQrDownload(c._id);
                                    }}
                                    className={`ml-auto whitespace-nowrap flex items-center gap-4 text-12 self-start font-700 text-darwin-accent-bright-blue-800 ${
                                        abhaQrStatus.status ? '' : 'cursor-pointer'
                                    }`}
                                >
                                    {abhaQrStatus.status &&
                                        abhaQrStatus.cid === c._id &&
                                        loaderIcon(
                                            'w-16 h-16 fill-darwin-accent-bright-blue-800 font-700 animate-spin',
                                        )}
                                    Download QR
                                </span>
                            ) : null
                        ) : (
                            <span
                                onClick={registerNow}
                                className="ml-auto whitespace-nowrap cursor-pointer text-13 self-start font-700 text-darwin-accent-bright-blue-800"
                            >
                                Register now
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ClinicDetailsContainer;
